import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { API_CONFIG } from '../helpers/constants';

const Redirect: React.FC = () => {
  const { shortLink } = useParams<{ shortLink: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolveRedirect = async () => {
      if (!shortLink) {
        setError('Invalid redirect link');
        setLoading(false);
        return;
      }

      try {
        // API endpoint: /api/v1/redirect/resolve/{short_link}
        // Must use absolute backend URL — the node static server returns 404 for /api/ paths.
        const apiUrl = `${API_CONFIG.API_BASE_URL}/api/v1/redirect/resolve/${shortLink}`;

        // This is a cross-origin request to the backend (absolute URL bypasses the dev proxy).
        // The endpoint is public — no auth cookies are required, so credentials: 'omit' is
        // intentional. Ensure the backend CORS config allows the frontend origin in production.
        const response = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'omit',
          headers: {
            'accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Response payload is a string (the redirect URL)
        const redirectUrl = await response.text();

        if (redirectUrl && redirectUrl.trim()) {
          // Redirect to the resolved URL
          window.location.href = redirectUrl.trim();
        } else {
          throw new Error('Empty redirect URL received');
        }
      } catch (err) {
        console.error('Error resolving redirect:', err);
        const errorMessage = err instanceof Error 
          ? err.message 
          : 'Failed to resolve redirect link';
        setError(errorMessage);
        setLoading(false);
      }
    };

    resolveRedirect();
  }, [shortLink]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Resolving redirect...
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Please wait while we redirect you.
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="textSecondary">
          The redirect link could not be resolved. Please check the link and try again.
        </Typography>
      </Container>
    );
  }

  // This should not be reached as we redirect immediately, but just in case
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Redirecting...
        </Typography>
      </Box>
    </Container>
  );
};

export default Redirect;
